import { useCallback, useMemo, useState } from "react";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Score } from "src/types";
import { useSearchCompanySectors, CompanySector } from "src/api/client-api/companySectors/useSupplierSectors";
import { isUrlValid, normalizeUrl } from "../components/urlValidation";

export type CreatePageDialogPayload = {
    title: string;
    description?: string;
    downloadOnlyOnSsqValidated?: boolean;
    allowedRoles: string[];
    url?: string;
    sectors?: string[];
    score?: string[];
    entity?: string[];
};

export const useCreatePageDialogController = (args: {
    onCreatePage: (payload: CreatePageDialogPayload) => void;
    onClose: () => void;
    isCreating: boolean;
    pageType: "CMS" | "PDF";
}) => {
    const { onCreatePage, onClose, isCreating, pageType } = args;

    const [newPageTitle, setNewPageTitle] = useState("");
    const [newPageDescription, setNewPageDescription] = useState("");
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [url, setUrl] = useState<string>("");
    const [sectorInput, setSectorInput] = useState<string>("");
    const [selectedSectorIds, setSelectedSectorIds] = useState<number[]>([]);

    const [selectedScore, setSelectedScore] = useState<string[]>([]);
    const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
    const [downloadOnlyOnSsqValidated, setDownloadOnlyOnSsqValidated] = useState(true);

    const foodpilotContext = useFoodpilotContext();
    const scores: Score[] =
        foodpilotContext?.getScoresByType ?
            foodpilotContext.getScoresByType("company")
        :   (foodpilotContext?.scores ?? []);

    const { companySectors: sectorOptions } = useSearchCompanySectors(sectorInput);

    const sectorIris = useMemo(() => {
        return selectedSectorIds.length > 0 ? selectedSectorIds.map((id) => `/api/organization_sectors/${id}`) : [];
    }, [selectedSectorIds]);

    const selectedSectorOptions = useMemo(() => {
        return sectorOptions.filter((s) => selectedSectorIds.includes(s.id));
    }, [sectorOptions, selectedSectorIds]);

    const normalizedUrlForCreate = useMemo(() => {
        return normalizeUrl(
            url ||
                "/" +
                    newPageTitle
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                        .replace(/[^a-z0-9\-/]/g, ""),
        );
    }, [newPageTitle, url]);

    const isUrlInvalid = useMemo(() => {
        return url !== "" && !isUrlValid(url);
    }, [url]);

    const canCreate = useMemo(() => {
        return (
            !!newPageTitle.trim() &&
            !!url.trim() &&
            isUrlValid(
                url ||
                    "/" +
                        newPageTitle
                            .toLowerCase()
                            .replace(/\s+/g, "-")
                            .replace(/[^a-z0-9\-/]/g, ""),
            ) &&
            !isCreating
        );
    }, [isCreating, newPageTitle, url]);

    const reset = useCallback(() => {
        setNewPageTitle("");
        setNewPageDescription("");
        setSelectedRoles([]);
        setUrl("");
        setSectorInput("");
        setSelectedSectorIds([]);
        setSelectedScore([]);
        setSelectedEntity([]);
        setDownloadOnlyOnSsqValidated(true);
    }, []);

    const handleCreate = useCallback(() => {
        if (!newPageTitle.trim()) return;
        if (!isUrlValid(normalizedUrlForCreate)) return;

        onCreatePage({
            title: newPageTitle,
            description: pageType === "PDF" ? newPageDescription : undefined,
            downloadOnlyOnSsqValidated: pageType === "PDF" ? downloadOnlyOnSsqValidated : undefined,
            allowedRoles: selectedRoles,
            url: normalizedUrlForCreate,
            sectors: sectorIris,
            score: pageType === "PDF" ? selectedScore : undefined,
            entity: pageType === "PDF" ? selectedEntity : undefined,
        });

        reset();
    }, [
        newPageTitle,
        normalizedUrlForCreate,
        onCreatePage,
        pageType,
        newPageDescription,
        downloadOnlyOnSsqValidated,
        reset,
        sectorIris,
        selectedEntity,
        selectedRoles,
        selectedScore,
    ]);

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [onClose, reset]);

    return {
        newPageTitle,
        setNewPageTitle,
        newPageDescription,
        setNewPageDescription,
        selectedRoles,
        setSelectedRoles,
        url,
        setUrl,
        sectorInput,
        setSectorInput,
        selectedSectorIds,
        setSelectedSectorIds,
        selectedScore,
        setSelectedScore,
        selectedEntity,
        setSelectedEntity,
        downloadOnlyOnSsqValidated,
        setDownloadOnlyOnSsqValidated,
        scores,
        sectorOptions,
        selectedSectorOptions,
        isUrlInvalid,
        canCreate,
        handleCreate,
        handleClose,
        pageType,
    };
};

export type { CompanySector };
